import { useState } from "react";
import { Copy, X, Facebook, Twitter, Linkedin, Send, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const shareTargets = (url, text) => [
  {
    key: "twitter",
    name: "X (Twitter)",
    color: "bg-black",
    icon: Twitter,
    href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: "facebook",
    name: "Facebook",
    color: "bg-[#1877F2]",
    icon: Facebook,
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: "messenger",
    name: "Messenger",
    color: "bg-[#006AFF]",
    iconText: "M",
    href: `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=291494419107518&redirect_uri=${encodeURIComponent(url)}`,
  },
  {
    key: "whatsapp",
    name: "WhatsApp",
    color: "bg-[#25D366]",
    iconText: "W",
    href: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
  },
  {
    key: "telegram",
    name: "Telegram",
    color: "bg-[#26A5E4]",
    icon: Send,
    href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    color: "bg-[#0A66C2]",
    icon: Linkedin,
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    key: "reddit",
    name: "Reddit",
    color: "bg-[#FF4500]",
    iconText: "R",
    href: `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
  },
  {
    key: "email",
    name: "Email",
    color: "bg-slate-700",
    iconText: "@",
    href: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`,
  },
];

export const ShareSheet = ({ open, onClose, url, text, eventId }) => {
  const [aiCaption, setAiCaption] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  if (!open) return null;
  const effectiveText = aiCaption || text;
  const targets = shareTargets(url, effectiveText);

  const generateCaption = async () => {
    if (!eventId) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/share-caption", { event_id: eventId, platform: "generic" });
      setAiCaption(data.caption);
      toast.success("AI caption ready — now share");
    } catch (err) {
      console.error("[AI] caption failed", err);
      toast.error(err.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      onClose();
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-[#131A26] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl">Share this event</h3>
          <button onClick={onClose} data-testid="share-close" className="text-slate-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {eventId && (
          <div className="mb-5">
            <button
              type="button"
              onClick={generateCaption}
              disabled={aiLoading}
              data-testid="share-ai-caption"
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-amber-500/30 text-amber-100 hover:text-white hover:border-amber-400 transition-colors text-sm font-semibold disabled:opacity-50"
            >
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiLoading ? "Writing..." : aiCaption ? "Regenerate AI caption" : "Write caption with AI"}
            </button>
            {aiCaption && (
              <div className="mt-2 p-3 rounded-xl bg-[#0B0F17] border border-amber-500/25 text-xs text-amber-100 leading-relaxed" data-testid="share-ai-caption-text">
                {aiCaption}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 mb-4">
          {targets.map((t) => (
            <a
              key={t.key}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${t.key}`}
              onClick={() => setTimeout(onClose, 300)}
              className="flex flex-col items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
            >
              <span className={`w-12 h-12 rounded-full ${t.color} flex items-center justify-center text-white font-bold`}>
                {t.icon ? <t.icon size={20} /> : t.iconText}
              </span>
              <span className="text-[10px] text-slate-300 text-center">{t.name}</span>
            </a>
          ))}
        </div>

        <button
          onClick={copy}
          data-testid="share-copy-link"
          className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-sm font-semibold"
        >
          <Copy size={16} /> Copy link
        </button>
      </div>
    </div>
  );
};
